interface EndpointMetrics {
  count: number;
  totalTime: number;
  averageTime: number;
  minTime: number;
  maxTime: number;
}

interface EndpointEntry {
  endpoint: string;
  duration: number;
}

interface PerformanceMetrics {
  apiCalls: Record<string, EndpointMetrics>;
  cacheHits: number;
  cacheMisses: number;
  totalLoadTime: number;
  averageLoadTime: number;
  slowestEndpoint: EndpointEntry | null;
  fastestEndpoint: EndpointEntry | null;
}

interface PerformanceEvent {
  type: 'apiCall' | 'cacheHit' | 'cacheMiss';
  endpoint?: string;
  duration?: number;
  metrics: PerformanceMetrics;
}

interface PerformanceSummary extends PerformanceMetrics {
  cacheHitRatio: string | number;
  totalApiCalls: number;
}

type PerformanceObserver = (event: PerformanceEvent) => void;

class PerformanceMonitor {
  private metrics: PerformanceMetrics;
  private startTimes: Map<string, number>;
  private observers: Set<PerformanceObserver>;

  constructor() {
    this.metrics = {
      apiCalls: {},
      cacheHits: 0,
      cacheMisses: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      slowestEndpoint: null,
      fastestEndpoint: null,
    };

    this.startTimes = new Map();
    this.observers = new Set();
  }

  startTimer(endpoint: string): number {
    const startTime = performance.now();
    this.startTimes.set(endpoint, startTime);
    return startTime;
  }

  endTimer(endpoint: string): number | undefined {
    const startTime = this.startTimes.get(endpoint);
    if (!startTime) return;

    const duration = performance.now() - startTime;
    this.startTimes.delete(endpoint);

    if (!this.metrics.apiCalls[endpoint]) {
      this.metrics.apiCalls[endpoint] = {
        count: 0,
        totalTime: 0,
        averageTime: 0,
        minTime: Infinity,
        maxTime: 0,
      };
    }

    const endpointMetrics = this.metrics.apiCalls[endpoint];
    endpointMetrics.count++;
    endpointMetrics.totalTime += duration;
    endpointMetrics.averageTime = endpointMetrics.totalTime / endpointMetrics.count;
    endpointMetrics.minTime = Math.min(endpointMetrics.minTime, duration);
    endpointMetrics.maxTime = Math.max(endpointMetrics.maxTime, duration);

    this.metrics.totalLoadTime += duration;
    this.metrics.averageLoadTime = this.metrics.totalLoadTime /
      Object.values(this.metrics.apiCalls).reduce((sum, m) => sum + m.count, 0);

    if (!this.metrics.slowestEndpoint || duration > this.metrics.slowestEndpoint.duration) {
      this.metrics.slowestEndpoint = { endpoint, duration };
    }
    if (!this.metrics.fastestEndpoint || duration < this.metrics.fastestEndpoint.duration) {
      this.metrics.fastestEndpoint = { endpoint, duration };
    }

    this.notifyObservers({
      type: 'apiCall',
      endpoint,
      duration,
      metrics: this.metrics,
    });

    return duration;
  }

  recordCacheHit(): void {
    this.metrics.cacheHits++;
    this.notifyObservers({
      type: 'cacheHit',
      metrics: this.metrics,
    });
  }

  recordCacheMiss(): void {
    this.metrics.cacheMisses++;
    this.notifyObservers({
      type: 'cacheMiss',
      metrics: this.metrics,
    });
  }

  getCacheHitRatio(): string | number {
    const total = this.metrics.cacheHits + this.metrics.cacheMisses;
    return total > 0 ? (this.metrics.cacheHits / total * 100).toFixed(1) : 0;
  }

  getPerformanceSummary(): PerformanceSummary {
    return {
      ...this.metrics,
      cacheHitRatio: this.getCacheHitRatio(),
      totalApiCalls: Object.values(this.metrics.apiCalls).reduce((sum, m) => sum + m.count, 0),
    };
  }

  addObserver(observer: PerformanceObserver): void {
    this.observers.add(observer);
  }

  removeObserver(observer: PerformanceObserver): void {
    this.observers.delete(observer);
  }

  private notifyObservers(event: PerformanceEvent): void {
    this.observers.forEach(observer => {
      try {
        observer(event);
      } catch (error) {
        console.error('Error in performance observer:', error);
      }
    });
  }

  reset(): void {
    this.metrics = {
      apiCalls: {},
      cacheHits: 0,
      cacheMisses: 0,
      totalLoadTime: 0,
      averageLoadTime: 0,
      slowestEndpoint: null,
      fastestEndpoint: null,
    };
    this.startTimes.clear();
  }

  exportMetrics(): PerformanceSummary {
    const summary = this.getPerformanceSummary();
    console.table(summary);
    return summary;
  }
}

const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;

export const withPerformanceMonitoring = (apiCall: (...args: any[]) => Promise<any>, endpoint: string) => {
  return async (...args: any[]): Promise<any> => {
    performanceMonitor.startTimer(endpoint);
    try {
      const result = await apiCall(...args);
      performanceMonitor.endTimer(endpoint);
      return result;
    } catch (error) {
      performanceMonitor.endTimer(endpoint);
      throw error;
    }
  };
};

export const monitorCacheOperation = (isHit: boolean): void => {
  if (isHit) {
    performanceMonitor.recordCacheHit();
  } else {
    performanceMonitor.recordCacheMiss();
  }
};

export type { PerformanceMetrics, PerformanceEvent, PerformanceSummary };
